import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export function Tooltip({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  const trigger = useRef<HTMLSpanElement>(null);
  const content = useRef<HTMLSpanElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState({ left: 8, top: 8 });
  const open = (hovered || focused) && !dismissed;
  function enter() {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setHovered(true);
    setDismissed(false);
  }
  function leave() {
    leaveTimer.current = setTimeout(() => setHovered(false), 120);
  }
  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    []
  );
  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      const anchor = trigger.current?.getBoundingClientRect();
      const tip = content.current?.getBoundingClientRect();
      if (!anchor || !tip) return;
      setPosition({
        left: Math.max(8, Math.min(anchor.left, window.innerWidth - tip.width - 8)),
        top:
          anchor.bottom + tip.height + 8 < window.innerHeight
            ? anchor.bottom + 4
            : Math.max(8, anchor.top - tip.height - 4),
      });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, children]);
  const tooltip = (
    <span
      ref={content}
      id={id}
      role="tooltip"
      className="info-tooltip-content"
      style={position}
      hidden={!open}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      {children}
    </span>
  );
  return (
    <span className="info-tooltip" onMouseEnter={enter} onMouseLeave={leave}>
      <span
        ref={trigger}
        className="info-tooltip-trigger"
        tabIndex={0}
        aria-label={label}
        aria-describedby={id}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            event.stopPropagation();
            setDismissed(true);
          }
        }}
      >
        <span aria-hidden="true">ⓘ</span>
      </span>
      {typeof document === "undefined" ? tooltip : createPortal(tooltip, document.body)}
    </span>
  );
}
